import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { 
  useMediaQuery, 
  useTheme, 
  Box, 
  Typography, 
  IconButton,    
  CircularProgress, 
  Tooltip, 
  ToggleButton, 
  ToggleButtonGroup, 
  FormControlLabel, 
  Switch,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import DownloadIcon from '@mui/icons-material/Download';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { setCrewBlockHours } from "state";
import { LAYOUT_HEIGHT } from 'state/constants';
import { 
  fetchGroupedLegMembersByCrewCode,
  fetchGroupedRosterDetailsByCrewCode,
  fetchCrewBlockHours, 
  fetchCrewTotalHours,
} from "state/api";
import { filterCrewMember } from "state/helpers";
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import LegMembersDialog from 'components/LegMembersDialog';
import MetricTree from './MetricTree';


const RosterAnaltics = () => {
  useAuthCheck();
  const dispatch = useDispatch();  
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  const canView = hasPermission(user?.permissions, "RosterAnalytics", "view", false);
  const canSwitch = hasPermission(user?.permissions, "RosterAnalytics", "switch", false);

  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  const [isGridStretched, setIsGridStretched] = useState(true);
  const [view, setView] = useState('bhView');
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ isActive: 'ALL' });
  const defaultFilters = {
    isActive: 'ALL',
  };
  const [openLegMembersDialog, setOpenLegMembersDialog] = useState(false);
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
 
  const crewRanksData = useSelector((state) => state.crewRanks);
  const crewBlockHoursData = useSelector((state) => state.crewBlockHours);
  const [crewTotalHours, setCrewTotalHours] = useState([]);

  // const [initialDateRange, setInitialDateRange] = useState(null);
  // const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {    
    const handleCrewBlockHours = async () => {      
      try {
        if (!startDate || !endDate) return null; 

        setError(null);
        setLoading(true);

        const { data } = await fetchCrewBlockHours(startDate, endDate);
        if (data && data.length > 0) {        
          dispatch(setCrewBlockHours({ crewBlockHours: data }));
        }
      } catch (error) {
        setError(error)
        toast.error(error?.message || "Failed to fetch crew block hours!");
      } finally {
        setLoading(false);
      }
    };
  
    // if (!hasLoadedOnce) {
    //   if (crewBlockHoursData && crewBlockHoursData.length > 0) {
    //     // Có dữ liệu sẵn → không fetch
    //     setInitialDateRange({ startDate, endDate });
    //     setHasLoadedOnce(true);
    //     return;
    //   }

    //   // Không có dữ liệu → fetch lần đầu
    //   handleCrewBlockHours();
    //   setInitialDateRange({ startDate, endDate });
    //   setHasLoadedOnce(true);
    //   return;
    // }

    // // Sau lần đầu, nếu khoảng thời gian đổi → fetch lại
    // if (initialDateRange && (startDate !== initialDateRange.startDate ||
    //   endDate !== initialDateRange.endDate)
    // ) {
    //   handleCrewBlockHours();
    //   setInitialDateRange({ startDate, endDate });
    // }  
    handleCrewBlockHours();
  }, [startDate, endDate, dispatch]); 

  const handleRosterDetails = useCallback(async () => {
    if (!startDate || !endDate) return [];
    
    try {
      setError(null);
      setLoading(true);      

      const { data } = await fetchCrewTotalHours(startDate, endDate);
      if (data) {        
        setCrewTotalHours(data);
      }
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch crew total hours!");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate])

  const filteredData = useMemo(() => {
    const data = view === 'bhView' ? crewBlockHoursData : crewTotalHours;

    if (!Array.isArray(data)) return [];
    if (!filters) return data;
    return data.filter(member => filterCrewMember(member, filters));
  }, [crewBlockHoursData, crewTotalHours, filters, view]);

  
  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };
  
  const handleViewClick = useCallback(async (param) => {
    if (!startDate || !endDate) return [];

    const crewCode = param?.crewCode || '';
    const crewName = param?.crewName || '';
    setSelectedCrewCode(crewCode);
    setSelectedCrewName(crewName);
    let result = [];

    try {
      result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);       
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
      return;
    } catch {      
      setOpenRosterDetailsDialog(false);      
    }

    try {
      result = await fetchGroupedLegMembersByCrewCode(crewCode, startDate, endDate);
      setGroupedCrewRosters(result);
      setOpenLegMembersDialog(true);
      return;
    } catch (error) {      
      setOpenLegMembersDialog(false);
    } 
  }, [startDate, endDate]);

  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };

  const handleCloseLegMembersDialog = () => {
    setGroupedCrewRosters([]);
    setOpenLegMembersDialog(false);
  };

  const buildColumns = useCallback((type) => {
    const baseCols = [
      {
        field: "no",
        headerName: "No",
        filterable: false,      
        sortable: false,
        ...isGridStretched ? { width: 60 } : { flex: 1 },
        renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      },    
      {
        field: "crewCode",
        headerName: "Code",
        ...isGridStretched ? { width: 80 } : { flex: 1 },
      },    
      {
        field: "crewName",
        headerName: "CrewName",
        cellClassName: "name-column--cell",
        ...isGridStretched ? { width: 280 } : { flex: 4 },
        renderCell: (params) => {
          const gender = params.row.gender; 
          return (
            <Box
              sx={{
                color: gender === 'FM' 
                  ? theme.palette.secondary.main 
                  : theme.palette.primary.dark,
              }}
            >
              {params.value}
            </Box>
          );
        },
      },
      {
        field: "post",
        headerName: "Post",
        ...isGridStretched ? { width: 60 } : { flex: 1 },
      },
      {
        field: "base",
        headerName: "Base",
        ...isGridStretched ? { width: 60 } : { flex: 1 },
      },
      {
        field: "trainingQuals",
        headerName: "Trainer",
        ...isGridStretched ? { width: 60 } : { flex: 1 },
      },
      {
        field: "quals",
        headerName: "Quals",
        ...(() => {
          return isGridStretched ? { width: 150 } : { flex: 2 };
        })(),
      },
      {
        field: "airHours",
        headerName: "AirHours",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params > 0 ? parseFloat(params).toFixed(2) : 0;
        },
      },
      {
        field: "dhdHours",
        headerName: "PaxHours",
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params > 0 ? parseFloat(params).toFixed(2) : 0; 
        },
      },
      {
        field: "dhdSectors",
        headerName: "PaxSectors",
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },
      {
        field: "blockHours",
        headerName: "BH",
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params > 0 ? parseFloat(params).toFixed(2) : 0;
        },
      },
      {
        field: "sectors",
        headerName: "Sectors",
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },
      {
        field: "instructorSimHours",
        headerName: "InstructorSimHours",
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 120 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0; 
        },
      },
      {
        field: "totalHours",
        headerName: "TotalHours",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 100 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0; 
        },
      },      
      {
        field: "totalHoursAvg",
        headerName: "TotalHoursAvg",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 105 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0;
        },
      },
      {
        field: "totalHoursDeviation",
        headerName: "TotalHoursDev",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 105 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0;
        },
      },
      {
        field: "traineeSimHours",
        headerName: "TraineeSimHours",
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 110 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0; 
        },
      },
      {
        field: "perdiemHours",
        headerName: "PerdiemHours",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 100 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0;
        },
      },
      {
        field: "domPerdiemHours",
        headerName: "DomPerdiemHours",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 100 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0;
        },        
      },
      {
        field: "intPerdiemHours",
        headerName: "IntPerdiemHours",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 100 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(2) : 0;
        },        
      },
      {
        field: "arrList",
        headerName: "ArrivalList",
        sortable: false,
        ...isGridStretched ? { width: 100 } : { flex: 1.8 },
        renderCell: (params) => {
          const value = params.value || {};
          const entries = Object.entries(value).sort((a, b) => b[1] - a[1]);
          if (!entries.length) return "";

          return (
            <Box
              sx={{
                height: '100%',
                alignItems: 'flex-start',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              style={{ lineHeight: 1.4 }}
            >
              {entries.map(([k, v], index) => (
                <div key={index}>{k}: {v}</div>
              ))}
            </Box>
          );
        },
      },
      {
        field: "sectorList",
        headerName: "SectorList",
        sortable: false,
        ...isGridStretched ? { width: 120 } : { flex: 2 },
        renderCell: (params) => {
          const value = params.value || {};
          const entries = Object.entries(value).sort((a, b) => {
            // 1. Sort theo value DESC
            if (b[1] !== a[1]) {
              return b[1] - a[1];
            }

            // 2. Value bằng nhau → sort theo key ASC
            return a[0].localeCompare(b[0]);
          });
          if (!entries.length) return "";

          return (
            <Box
              sx={{
                height: '100%',
                alignItems: 'flex-start',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              style={{ lineHeight: 1.4 }}
            >
              {entries.map(([k, v], index) => (                
                <div key={index}>{k}: {v}</div>
              ))}
            </Box>
          );
        },
      },
      {
        field: "flyingDays",
        headerName: "FlyingDays",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },
      {
        field: "flyingDaysAvg",
        headerName: "FlyingDaysAvg",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 100 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(1) : 0;
        },
      },
      {
        field: "flyingDaysDeviation",
        headerName: "FlyingDaysDev",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 100 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(1) : 0;
        },
      },
      {
        field: "nonFlyingDays",
        headerName: "NonFlyingDays",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 100 } : { flex: 1.2 },
      },
      {
        field: "nonFlyingDaysAvg",
        headerName: "NonFlyingDaysAvg",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 120 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(1) : 0;
        },
      },
      {
        field: "nonFlyingDaysDeviation",
        headerName: "NonFlyingDaysDev",      
        cellClassName: 'right-align-cell',
        align: 'right',
        ...isGridStretched ? { width: 120 } : { flex: 1.2 },
        valueFormatter: (params) => {
          return params ? parseFloat(params).toFixed(1) : 0;
        },
      },
      {
        field: "woclSectors",
        headerName: "WoclSectors",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },
      {
        field: "weekendRoutes",
        headerName: "WeekendRoutes",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 110 } : { flex: 1.2 },
      },
      {
        field: "standbyCount",
        headerName: "Standby",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },   
      {
        field: "noshowCount",
        headerName: "NoShow",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },
      {
        field: "sickCount",
        headerName: "Sick",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },   
      {
        field: "sickOnHolidayAndWeekendCount",
        headerName: "SickOnHoliWeek",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 110 } : { flex: 1.2 },
      },
      {
        field: "fixedOffCount",
        headerName: "FixedOff",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },
      {
        field: "fixedOffOnHolidayAndWeekendCount",
        headerName: "FixedOffOnHoliWeek",      
        cellClassName: 'right-align-cell',
        align: 'right',        
        ...isGridStretched ? { width: 130 } : { flex: 1.2 },
      }, 
      {
        field: "scheduleCodeCount",
        headerName: "Codes",
        sortable: false,
        ...isGridStretched ? { width: 100 } : { flex: 1.8 },
        renderCell: (params) => {
          const value = params.value || {};

          const entries = Object.entries(value).sort((a, b) => b[1] - a[1]);

          if (!entries.length) return "";

          return (
            <Box
              sx={{
                height: '100%',
                alignItems: 'flex-start',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              style={{ lineHeight: 1.4 }}
            >
              {entries.map(([k, v]) => (
                <div key={k}>{k}: {v}</div>
              ))}
            </Box>
          );
        },
      },
    ];
    const commonCols = [
      {
        field: "isActive",
        headerName: "IsActive",
        ...isGridStretched ? { width: 90 } : { flex: 1.2 },
      },
      (canView && {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: 80,
        renderCell: (params) => {
          return (
            <FlexCenter
              gap={1}
              sx={{ width: '100%', height: '100%' }}
            >
              <Tooltip title="View">
                <IconButton
                  aria-label="View"
                  sx={{
                    color: theme.palette.primary.dark,
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.green.deep,
                    }
                  }}
                  onClick={() => {
                    if (!canView) return;
                    handleViewClick(params.row);
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            </FlexCenter>
          );
        },
      })
    ].filter(Boolean);

    if (type === 'bhView') {
      return [
        ...baseCols,
        {
          field: "lineTrainingBlockHours",
          headerName: "LTBH",
          cellClassName: 'right-align-cell',
          align: 'right',
          ...isGridStretched ? { width: 90 } : { flex: 1.2 },
          valueFormatter: (params) => {
            return params > 0 ? parseFloat(params).toFixed(2) : 0;
          },          
        },
        {
          field: "lineTrainingSectors",
          headerName: "LTSectors",
          cellClassName: 'right-align-cell',
          align: 'right',
          ...isGridStretched ? { width: 90 } : { flex: 1.2 },
        },
        {
          field: "observeBlockHours",
          headerName: "ObsBH",
          cellClassName: 'right-align-cell',
          align: 'right',
          ...isGridStretched ? { width: 90 } : { flex: 1.2 },
          valueFormatter: (params) => {
            return params > 0 ? parseFloat(params).toFixed(2) : 0;
          },          
        },
        {
          field: "observeSectors",
          headerName: "ObsSectors",
          cellClassName: 'right-align-cell',
          align: 'right',
          ...isGridStretched ? { width: 90 } : { flex: 1.2 },
        },
        ...commonCols,
      ];
    }

    if (type === 'thView') {
      return [    
        ...baseCols,        
        ...commonCols,
      ];
    }

    return [...baseCols, ...commonCols];
  }, [
    canView,
    theme, 
    isGridStretched,
    handleViewClick, 
  ]);

  const hiddenColumns = {  
    _id: false,    
    post: false,
    base: false,
    trainingQuals: false,
    quals: false,
    airHours: false,
    dhdHours: false,
    dhdSectors: false,
    // instructorSimHours: false,
    // averageTotalHours: false,
    // gapTotalHours: false,
    lineTrainingBlockHours: false,
    lineTrainingSectors: false,
    observeBlockHours: false,
    observeSectors: false,
    traineeSimHours: false,
    domPerdiemHours: false,
    intPerdiemHours: false,
    isActive: false,
  };
  
  const nonMobileHiddenColumns = {
    _id: false,    
    trainingQuals: false,
    quals: false,
    // airHours: false,
    // dhdHours: false,
    // dhdSectors: false,
    // averageTotalHours: false,
    // gapTotalHours: false,
    // lineTrainingBlockHours: false,
    // lineTrainingSectors: false,
    // observeBlockHours: false,
    // observeSectors: false,
    // traineeSimHours: false,
    domPerdiemHours: false,
    intPerdiemHours: false,
    isActive: false,
  };

  const {
    baseOptions,
    postOptions,
    trainingQualOptions,
    isActiveOptions,
  } = useMemo(() => {
    if (!Array.isArray(crewBlockHoursData) || crewBlockHoursData.length === 0) {
      return {
        baseOptions: [],
        postOptions: [],
        trainingQualOptions: [],
        isActiveOptions: [],
      };
    }

    const baseSet = new Set();
    const postSet = new Set();
    const trainingQualSet = new Set();
    const isActiveSet = new Set();

    crewBlockHoursData.forEach(m => {
      m.base && baseSet.add(m.base);
      m.post && postSet.add(m.post);
      m.isActive !== undefined && isActiveSet.add(m.isActive);

      m.trainingQuals?.forEach(q => q && trainingQualSet.add(q));
    });

    return {
      baseOptions: [...baseSet].sort(),

      postOptions: [...postSet]
        .map(post => crewRanksData.find(r => r.rankCode === post))
        .filter(Boolean)
        .sort((a, b) => a.rankPriority - b.rankPriority)
        .map(r => r.rankCode),

      trainingQualOptions: [...trainingQualSet].sort(),

      isActiveOptions: [...isActiveSet].sort(),
    };
  }, [crewBlockHoursData, crewRanksData]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Roster Analytics" 
          subtitle={loading ? 'Loading...' : `List of ${filteredData.length || 0} Crews`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>           
            <FormControlLabel
              label="Grid Stretch"
              sx={{ display: 'none' }}
              control={
                <Switch                  
                  checked={isGridStretched}
                  onChange={() => setIsGridStretched(prev => !prev)}
                />
              }        
            />

            {canSwitch && (
              <ToggleButtonGroup
                aria-label="View Toggle"
                value={view}
                exclusive
                onChange={(e, value) => value && setView(value)}
              >
                {[
                  { value: "bhView", icon: <ViewQuiltIcon /> },
                  { value: "thView", icon: <ViewModuleIcon />, onClick: handleRosterDetails },
                ].map(({ value, icon, onClick }) => (
                  <ToggleButton
                    aria-label={value}
                    size="small"
                    key={value}
                    value={value}
                    onClick={onClick}
                  >
                    {icon}
                  </ToggleButton>
                ))}              
              </ToggleButtonGroup>
            )}            
            <Tooltip title="Download Excel file" arrow> 
              <span>
                <StyledButton 
                  onClick={() => {
                    exportToExcel(view === 'bhView' ? crewBlockHoursData : crewTotalHours, "RosterStats");
                    logClientAction("EXPORT_REPORT", { fileName: "RosterAnalytics", format: "Excel", startDate, endDate });
                  }}
                >
                  <DownloadIcon />
                </StyledButton>
              </span>          
            </Tooltip>
            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                    
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          {!isMobile ? (
            <Box 
              height={LAYOUT_HEIGHT} 
              sx={{ gridColumn: "span 12", overflowY: 'auto' }}
            >
              <DataGridComponent
                rows={filteredData}
                columns={buildColumns(view)}
                loading={loading}
                props={{
                  initialState: {
                    pagination: { paginationModel: { pageSize: 100 } },
                    columns: { columnVisibilityModel: !isDesktop ? hiddenColumns : nonMobileHiddenColumns },
                    sorting: { sortModel: [{ field: "totalHours", sort: "desc" }] },
                  },
                }}
              />
            </Box>  
          ) : (
            <Box 
              ref={scrollRef}
              height={LAYOUT_HEIGHT} 
              display="flex" 
              flexDirection="column" 
              gridColumn={"span 12"}
              overflow="auto"
              sx={{ 
                borderRadius: 1,
                backgroundColor: "background.default",
              }} 
            >
              <MetricTree data={filteredData} />

              <ScrollToTop scrollRef={scrollRef} />  
            </Box>
          )}    
        </Box>
      )}

      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          isActiveOptions={isActiveOptions}
          currentFilters={filters}
        />
      }
      
      {openRosterDetailsDialog &&
        <RosterDetailsDialog 
          open={openRosterDetailsDialog}
          onClose={handleCloseRosterDetailsDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewRosters={groupedCrewRosters}
        />
      }

      {openLegMembersDialog &&
        <LegMembersDialog 
          open={openLegMembersDialog}
          onClose={handleCloseLegMembersDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewLegs={groupedCrewRosters}
        />
      }
    </Box>
  );
};

export default RosterAnaltics;
